import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload } from 'lucide-react';
import { API } from '../App';
import { useT } from '../i18n';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

export const IMPORT_EXAMPLE = 'subid,payout,tid,status\nabc123,25,,sale\ndef456,0,t-17,rejected';

const readAs = (file, how) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(r.error);
    if (how === 'dataurl') r.readAsDataURL(file);
    else r.readAsText(file);
  });

export const isXlsx = (name) => /\.xlsx$/i.test(String(name || ''));

/*
  Conversion import (POST /api/conversions/import): paste lines or pick a CSV /
  TXT / XLSX file. With "Send on" (on by default) a new conversion or a new
  status goes to the Conversion API and the campaign postbacks, the same way as
  an incoming postback; the same status again sends nothing.
*/
export default function ConversionImport({ onDone }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [xlsx, setXlsx] = useState(null); // { name, data }
  const [dispatch, setDispatch] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setText('');
    setXlsx(null);
    setResult(null);
    setDispatch(true);
  };

  const pickFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    try {
      if (isXlsx(file.name)) {
        setXlsx({ name: file.name, data: await readAs(file, 'dataurl') });
        setText('');
      } else {
        setXlsx(null);
        setText(String(await readAs(file, 'text')));
      }
    } catch (_err) {
      toast.error(t('Could not read the file'));
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const body = xlsx ? { xlsx_base64: xlsx.data, dispatch } : { data: text, dispatch };
      const r = await axios.post(API + '/conversions/import', body);
      setResult(r.data || {});
      if (onDone) onDone(r.data);
    } catch (err) {
      toast.error((err && err.response && err.response.data && err.response.data.detail) || t('Import failed'));
    } finally {
      setBusy(false);
    }
  };

  const reasonText = (r) =>
    r === 'click_not_found'
      ? t('No click with this subid in your workspace')
      : r === 'status_transition_not_allowed'
      ? t('This status change is not allowed by your status rules')
      : r;

  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="conv-import-open">
        <Upload className="w-4 h-4 mr-2" />
        {t('Import conversions')}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Import conversions')}</DialogTitle>
            <DialogDescription>
              {t('One conversion per line: subid,payout,tid,status. subid is the click id; the rest is optional (status sale by default). A header line is skipped.')}
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="space-y-3" data-testid="conv-import-result">
              <p className="text-sm">
                {t('Created: {created}, updated: {updated}, skipped: {skipped}, blocked: {blocked}.', result)}
              </p>
              <p className="text-sm text-muted-foreground">
                {result.dispatch
                  ? t('Sent on to ad platforms and campaign postbacks: {count}.', { count: result.dispatched || 0 })
                  : t('Nothing was sent to ad platforms or campaign postbacks.')}
              </p>
              {(result.problems || []).length ? (
                <Table data-testid="conv-import-problems">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('Line')}</TableHead>
                      <TableHead>subid</TableHead>
                      <TableHead>{t('Why')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result.problems.map((p) => (
                      <TableRow key={p.line}>
                        <TableCell>{p.line}</TableCell>
                        <TableCell className="font-mono text-xs">{p.subid}</TableCell>
                        <TableCell className="text-sm">{reasonText(p.reason)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : null}
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={reset}>{t('Import more')}</Button>
                <Button onClick={() => setOpen(false)}>{t('Close')}</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="conv-import-file">{t('File (CSV, TXT or XLSX)')}</Label>
                <input id="conv-import-file" type="file" accept=".csv,.txt,.xlsx" onChange={pickFile} className="block text-sm" />
                {xlsx ? <p className="text-xs text-muted-foreground">{t('XLSX: the first sheet, first four columns.')} {xlsx.name}</p> : null}
              </div>
              {!xlsx ? (
                <div className="space-y-1">
                  <Label htmlFor="conv-import-text">{t('Or paste the lines')}</Label>
                  <Textarea id="conv-import-text" data-testid="conv-import-text" rows={8} className="font-mono text-xs" placeholder={IMPORT_EXAMPLE} value={text} onChange={(e) => setText(e.target.value)} />
                </div>
              ) : null}
              <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
                <div>
                  <Label htmlFor="conv-import-dispatch">{t('Send to ad platforms and campaign postbacks')}</Label>
                  <p className="text-xs text-muted-foreground">
                    {t('A new conversion or a new status goes on like an incoming postback: Conversion API and the campaign postbacks. The same status again sends nothing.')}
                  </p>
                </div>
                <Switch id="conv-import-dispatch" data-testid="conv-import-dispatch" checked={dispatch} onCheckedChange={setDispatch} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)}>{t('Cancel')}</Button>
                <Button onClick={submit} disabled={busy || (!xlsx && !text.trim())} data-testid="conv-import-submit">
                  {t('Import')}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
